import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { salesOrderAPI, settingsInvoicePrinterAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const Invoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();

  // Read ?print=true from URL
  const query = new URLSearchParams(window.location.search);
  const autoPrint = query.get("print") === "true";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printerSettings, setPrinterSettings] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [previewCanvas, setPreviewCanvas] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  const printedRef = React.useRef(false);

  useEffect(() => {
    loadOrder();
    loadPrinterSettings();
  }, [id]);

  const loadOrder = async () => {
    try {
      const response = await salesOrderAPI.getOne(id);
      setOrder(response.data.data || response.data);
    } catch (error) {
      showError('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const loadPrinterSettings = async () => {
    try {
      const response = await settingsInvoicePrinterAPI.get();
      const settings = response.data.data || response.data;
      setPrinterSettings(settings);
    } catch (error) {
      showWarning('Could not load printer settings');
    }
  };

  // Auto-print to thermal printer when ?print=true
  useEffect(() => {
    if (!loading && autoPrint && printerSettings) {
      if (!printedRef.current) {
        printedRef.current = true;
        setTimeout(() => handlePrintToThermal(), 500);
      }
    }
  }, [loading, autoPrint, printerSettings]);

  // Render preview canvas
  useEffect(() => {
    if (order && printerSettings) {
      renderThermalInvoice().then(canvas => {
        setPreviewCanvas(canvas);
      });
    }
  }, [order, printerSettings]);

  const handlePrint = async () => {
    // Print to thermal printer (like lab tasks)
    await handlePrintToThermal();
  };

  const renderThermalInvoice = async () => {
    if (!printerSettings || !order) return null;

    const canvas = document.createElement('canvas');
    let width = parseInt(printerSettings.width || '384', 10);
    const maxW = 648;
    width = Math.min(Math.max(width, 200), maxW);
    if (width % 8 !== 0) width += (8 - (width % 8));

    const scale = parseFloat(printerSettings.font_scale || '2.0');
    const padding = Math.round(16 * scale);
    const lineHeight = Math.round(24 * scale);
    const margin = Math.round(16 * scale);

    // Detect if RTL (Arabic) language is active
    const isRTL = i18n.language === 'ar' || i18n.language?.startsWith('ar-');

    // Load logo if exists - using blob approach to avoid canvas tainting (like Lab printing)
    let logoImage = null;
    let logoHeight = 0;
    let logoWidth = 0;
    if (printerSettings.show_logo && printerSettings.logo_url) {
      try {
        let logoUrl = printerSettings.logo_url;

        // Handle different logo URL formats
        if (logoUrl) {
          let filename = null;

          // Extract filename from any format
          if (logoUrl.includes('/storage/logos/')) {
            filename = logoUrl.split('/storage/logos/')[1];
          } else if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
            filename = logoUrl.split('/').pop();
          } else if (logoUrl.startsWith('/')) {
            filename = logoUrl.split('/').pop();
          } else {
            filename = logoUrl;
          }

          // Use the API endpoint for CORS support
          if (filename) {
            // Get backend base URL from environment or derive from API calls
            const backendUrl = process.env.REACT_APP_API_BASE_URL || 'https://moustache-uae.net';
            logoUrl = `${backendUrl}/api/logo/${filename}`;
          }
        }

        // Helper function to load image
        const loadImage = (src, useAnon = true) => new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = useAnon ? 'anonymous' : 'use-credentials';
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error(`Image load failed: ${src}`));
          img.src = src;
        });

        // Try fetching as blob first to avoid CORS/tainted canvas issues (Lab approach)
        try {
          const res = await fetch(logoUrl);
          if (res.ok) {
            const blob = await res.blob();
            const localUrl = URL.createObjectURL(blob);
            logoImage = await loadImage(localUrl, true);
          } else {
            throw new Error(`HTTP ${res.status}`);
          }
        } catch (_error1) {
          try {
            // Try with anonymous CORS
            logoImage = await loadImage(logoUrl, true);
          } catch (_error2) {
            try {
              // Try with credentials
              logoImage = await loadImage(logoUrl, false);
            } catch (_error3) {
              // Logo failed to load - continue printing without logo
              logoImage = null;
            }
          }
        }

        if (logoImage) {
          const logoTargetWidth = parseInt(printerSettings.logo_width || '200', 10) * scale;
          const logoTargetHeight = parseInt(printerSettings.logo_height || '100', 10) * scale;
          const imgRatio = logoImage.width / logoImage.height;
          const targetRatio = logoTargetWidth / logoTargetHeight;

          if (imgRatio > targetRatio) {
            logoWidth = logoTargetWidth;
            logoHeight = Math.floor(logoTargetWidth / imgRatio);
          } else {
            logoWidth = Math.floor(logoTargetHeight * imgRatio);
            logoHeight = logoTargetHeight;
          }
        }
      } catch (err) {
        logoImage = null;
        logoHeight = 0;
        logoWidth = 0;
      }
    }

    const ctx = canvas.getContext('2d');

    // Get translations and company info from settings
    const companyNameAr = printerSettings.company_name_ar || 'موستاش جلابية';
    const companyNameEn = printerSettings.company_name_en || 'Mustash Jalabiya';
    const companyTaglineAr = printerSettings.company_tagline_ar || '';
    const companyTaglineEn = printerSettings.company_tagline_en || '';

    const labels = {
      companyName: i18n.language === 'ar' ? companyNameAr : companyNameEn,
      companyTagline: i18n.language === 'ar' ? companyTaglineAr : companyTaglineEn,
      invoiceNumber: t('salesOrders.orderNumber'),
      date: t('common.date'),
      deliveryDate: t('lab.printDeliveryDate') || 'تاريخ التسليم',
      customer: printerSettings.customer_name || t('accounting.customer'),
      phone: t('customers.phone') || 'الهاتف',
      // address: t('customers.address') || 'العنوان',
      quantity: t('accounting.quantity'),
      description: t('invoice.description') || 'الوصف',
      lineTotal: t('invoice.total') || 'الإجمالي',
      subtotal: t('accounting.subtotal'),
      discount: printerSettings.discount_name || t('accounting.discount'),
      fees: printerSettings.fees_name || t('salesOrders.fee'),
      vat: t('invoice.vat') || 'الضريبة',
      total: t('accounting.total'),
      paymentStatus: t('accounting.paymentStatus'),
      paidInFull: t('invoice.paidInFull') || 'مدفوع بالكامل',
      partialPayment: t('invoice.partialPayment') || 'دفع جزئي',
      unpaid: t('invoice.unpaid') || 'لم يتم الدفع',
      amountPaid: t('salesOrders.amountPaid') || 'المبلغ المدفوع',
      remaining: t('salesOrders.remaining') || 'المبلغ المتبقي',
      thankYou: t('invoice.thankYou') || 'شكراً لك',
      customerInfo: t('invoice.customerInfo') || 'معلومات العميل',
      currency: t('invoice.currency') || 'د.إ',
    };

    // Helper function to scale fonts
    const scaledFont = (baseSize) => Math.round(baseSize * scale);

    // Calculate item-level discounts for height estimation
    const itemsDiscountTotalEstimate = order.items?.reduce((sum, item) => {
      return sum + parseFloat(item.discount_amount || 0);
    }, 0) || 0;

    // Estimate height
    let estimatedHeight = padding;
    if (logoImage) estimatedHeight += logoHeight + Math.round(12 * scale); // Logo + spacing
    estimatedHeight += lineHeight * 2; // Company name
    if (printerSettings.show_invoice_number) estimatedHeight += lineHeight;
    if (printerSettings.show_invoice_date) estimatedHeight += lineHeight;
    if (order.expected_delivery_date) estimatedHeight += lineHeight;
    estimatedHeight += lineHeight; // Separator
    if (printerSettings.show_customer_name) estimatedHeight += lineHeight * 2;
    if (printerSettings.show_customer_phone && order.customer?.phone) estimatedHeight += lineHeight;
    // if (printerSettings.show_customer_address && order.customer?.address) estimatedHeight += lineHeight;
    estimatedHeight += lineHeight; // Separator
    if (printerSettings.show_item_details) estimatedHeight += lineHeight * (1 + (order.items?.length || 0));
    estimatedHeight += lineHeight; // Separator
    if (printerSettings.show_subtotal) estimatedHeight += lineHeight;
    if (itemsDiscountTotalEstimate > 0) estimatedHeight += lineHeight;
    if (parseFloat(order.discount_amount || 0) > 0) estimatedHeight += lineHeight;
    if (parseFloat(order.fee_amount || 0) > 0) estimatedHeight += lineHeight;
    if (parseFloat(order.vat_amount || 0) > 0) estimatedHeight += lineHeight;
    if (printerSettings.show_total) estimatedHeight += lineHeight;
    // Amount paid and remaining lines
    const tempAmountPaid = parseFloat(order.amount_paid || 0);
    const tempRemaining = parseFloat(order.total_price || 0) - tempAmountPaid;
    if (tempAmountPaid > 0) estimatedHeight += lineHeight;
    if (tempRemaining > 0) estimatedHeight += lineHeight;
    if (printerSettings.show_payment_status) estimatedHeight += lineHeight;
    estimatedHeight += lineHeight * 2; // Footer
    estimatedHeight += padding;

    canvas.width = width;
    canvas.height = estimatedHeight;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, estimatedHeight);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';

    let y = padding;

    // Helper to flip text alignment for RTL
    const getAlign = (align) => {
      if (!isRTL) return align;
      if (align === 'left') return 'right';
      if (align === 'right') return 'left';
      return align;
    };

    // Helper to get X position based on RTL
    const getXPos = (align) => {
      const flipped = getAlign(align);
      if (flipped === 'left') return margin;
      if (flipped === 'right') return width - margin;
      return width / 2;
    };

    const drawText = (text, size = 14, bold = false, align = 'center') => {
      ctx.font = `${bold ? 'bold ' : ''}${scaledFont(size)}px Arial, sans-serif`;
      ctx.textAlign = getAlign(align);
      ctx.fillText(text, getXPos(align), y);
      y += lineHeight;
    };

    const drawSeparator = () => {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin, y);
      ctx.lineTo(width - margin, y);
      ctx.stroke();
      y += lineHeight;
    };

    // Logo
    if (logoImage && logoHeight > 0 && logoWidth > 0) {
      const logoX = (width - logoWidth) / 2;
      ctx.drawImage(logoImage, logoX, y, logoWidth, logoHeight);
      y += logoHeight + Math.round(12 * scale);
    }

    // Company name
    if (printerSettings.show_company_info) {
      drawText(labels.companyName, 28, true);
      drawText(labels.companyTagline, 14);
    }

    // Invoice number and date
    drawSeparator();
    if (printerSettings.show_invoice_number) {
      drawText(`${labels.invoiceNumber}: ${order.order_number}`, 14, true, 'left');
    }
    if (printerSettings.show_invoice_date) {
      drawText(`${labels.date}: ${new Date(order.order_date).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}`, 14, false, 'left');
    }
    if (order.expected_delivery_date) {
      drawText(`${labels.deliveryDate}: ${new Date(order.expected_delivery_date).toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')}`, 14, false, 'left');
    }

    // Customer info
    if (printerSettings.show_customer_name && order.customer) {
      drawSeparator();
      drawText(labels.customer, 14, true, 'left');
      drawText(order.customer.name || 'N/A', 14, false, 'left');
    }
    if (printerSettings.show_customer_phone && order.customer?.phone) {
      drawText(`${labels.phone}: ${order.customer.phone}`, 12, false, 'left');
    }
    // if (printerSettings.show_customer_address && order.customer?.address) {
    //   drawText(`${labels.address}: ${order.customer.address}`, 12, false, 'left');
    // }

    // Items
    if (printerSettings.show_item_details && order.items?.length > 0) {
      drawSeparator();
      ctx.font = `bold ${scaledFont(14)}px Arial, sans-serif`;
      ctx.textAlign = getAlign('left');
      ctx.fillText(labels.quantity, getXPos('left'), y);
      ctx.fillText(labels.description, isRTL ? width - margin - Math.round(60 * scale) : margin + Math.round(60 * scale), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(labels.lineTotal, getXPos('right'), y);
      y += lineHeight;

      order.items?.forEach((item) => {
        const itemName = getItemName(item);
        const quantity = parseFloat(item.quantity).toFixed(2);
        const unitPrice = parseFloat(item.unit_price).toFixed(2);
        const lineTotal = (quantity * unitPrice).toFixed(2);
        const fabricType = item.fabric_sewing_type?.name ? ` (${item.fabric_sewing_type.name})` : '';

        ctx.font = `${scaledFont(12)}px Arial, sans-serif`;
        ctx.textAlign = getAlign('left');
        ctx.fillText(quantity, getXPos('left'), y);
        ctx.fillText(itemName + fabricType, isRTL ? width - margin - Math.round(60 * scale) : margin + Math.round(60 * scale), y);
        ctx.textAlign = getAlign('right');
        ctx.fillText(`${labels.currency} ${lineTotal}`, getXPos('right'), y);
        y += lineHeight;
      });
    }

    // Summary
    drawSeparator();

    // Calculate item-level discounts
    const itemsDiscountTotal = order.items?.reduce((sum, item) => {
      return sum + parseFloat(item.discount_amount || 0);
    }, 0) || 0;

    const orderDiscountAmount = parseFloat(order.discount_amount || 0);
    const orderFeeAmount = parseFloat(order.fee_amount || 0);

    const subtotal = parseFloat(order.total_price || 0) - parseFloat(order.vat_amount || 0);

    // Subtotal
    ctx.font = `${scaledFont(14)}px Arial, sans-serif`;
    ctx.textAlign = getAlign('left');
    ctx.fillText(labels.subtotal, getXPos('left'), y);
    ctx.textAlign = getAlign('right');
    ctx.fillText(`${labels.currency} ${subtotal.toFixed(2)}`, getXPos('right'), y);
    y += lineHeight;

    // Item-level Discount
    if (itemsDiscountTotal > 0) {
      ctx.textAlign = getAlign('left');
      ctx.fillText(`${labels.discount} (items)`, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`-${labels.currency} ${itemsDiscountTotal.toFixed(2)}`, getXPos('right'), y);
      y += lineHeight;
    }

    // Order-level Discount
    if (orderDiscountAmount > 0) {
      ctx.textAlign = getAlign('left');
      ctx.fillText(`${labels.discount} (order)`, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`-${labels.currency} ${orderDiscountAmount.toFixed(2)}`, getXPos('right'), y);
      y += lineHeight;
    }

    // Fees
    if (orderFeeAmount > 0) {
      ctx.textAlign = getAlign('left');
      ctx.fillText(labels.fees, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`+${labels.currency} ${orderFeeAmount.toFixed(2)}`, getXPos('right'), y);
      y += lineHeight;
    }

    // VAT
    if (parseFloat(order.vat_amount || 0) > 0) {
      ctx.textAlign = getAlign('left');
      ctx.fillText(`${labels.vat} (${parseFloat(order.vat_percentage || 0).toFixed(2)}%)`, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`${labels.currency} ${parseFloat(order.vat_amount).toFixed(2)}`, getXPos('right'), y);
      y += lineHeight;
    }

    // Total
    ctx.font = `bold ${scaledFont(16)}px Arial, sans-serif`;
    ctx.textAlign = getAlign('left');
    ctx.fillText(labels.total, getXPos('left'), y);
    ctx.textAlign = getAlign('right');
    ctx.fillText(`${labels.currency} ${parseFloat(order.total_price || 0).toFixed(2)}`, getXPos('right'), y);
    y += lineHeight;

    // Amount Paid and Remaining
    const totalAmount = parseFloat(order.total_price || 0);
    const amountPaidValue = parseFloat(order.amount_paid || 0);
    const remainingAmount = totalAmount - amountPaidValue;

    if (amountPaidValue > 0) {
      ctx.font = `${scaledFont(12)}px Arial, sans-serif`;
      ctx.textAlign = getAlign('left');
      ctx.fillText(labels.amountPaid, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`${labels.currency} ${amountPaidValue.toFixed(2)}`, getXPos('right'), y);
      y += lineHeight;
    }

    if (remainingAmount > 0) {
      ctx.font = `bold ${scaledFont(14)}px Arial, sans-serif`;
      ctx.fillStyle = '#FF6B6B';
      ctx.textAlign = getAlign('left');
      ctx.fillText(labels.remaining, getXPos('left'), y);
      ctx.textAlign = getAlign('right');
      ctx.fillText(`${labels.currency} ${remainingAmount.toFixed(2)}`, getXPos('right'), y);
      ctx.fillStyle = '#000000';
      y += lineHeight;
    }

    // Payment status
    if (printerSettings.show_payment_status) {
      const paymentStatus =
        order.payment_status === 'paid'
          ? labels.paidInFull
          : order.payment_status === 'partial'
          ? labels.partialPayment
          : labels.unpaid;
      ctx.font = `${scaledFont(12)}px Arial, sans-serif`;
      ctx.textAlign = getAlign('left');
      ctx.fillText(`${labels.paymentStatus}: ${paymentStatus}`, getXPos('left'), y);
      y += lineHeight;
    }

    // Footer
    drawSeparator();
    ctx.font = `bold ${scaledFont(14)}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(labels.thankYou, width / 2, y);
    y += lineHeight;
    ctx.font = `${scaledFont(12)}px Arial, sans-serif`;
    ctx.fillText(labels.customerInfo, width / 2, y);

    return canvas;
  };

  const handlePrintToThermal = async () => {
    // Get printer IP from settings or ask user
    let ip = printerSettings?.host || '';
    if (!ip) {
      ip = window.prompt('Enter Printer IP Address:');
      if (!ip) {
        showWarning('Printer IP is required');
        return;
      }
    }

    setPrinting(true);
    try {
      const canvas = await renderThermalInvoice();
      if (!canvas) throw new Error('Failed to render invoice');

      const width = canvas.width;
      const height = canvas.height;
      const wBytes = Math.ceil(width / 8);

      let img;
      let data;

      try {
        // Try to get image data directly from canvas
        img = canvas.getContext('2d').getImageData(0, 0, width, height);

        const xL = wBytes & 0xFF;
        const xH = (wBytes >> 8) & 0xFF;
        const yL = height & 0xFF;
        const yH = (height >> 8) & 0xFF;

        const header = [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH];
        data = new Uint8Array(header.length + wBytes * height);

        for (let i = 0; i < header.length; i++) data[i] = header[i];

        let offset = header.length;
        for (let row = 0; row < height; row++) {
          for (let byte = 0; byte < wBytes; byte++) {
            let b = 0;
            for (let bit = 0; bit < 8; bit++) {
              const x = byte * 8 + bit;
              if (x >= width) continue;
              const idx = (row * width + x) * 4;
              const r = img.data[idx];
              const g = img.data[idx + 1];
              const bl = img.data[idx + 2];
              const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
              const isBlack = luminance < 160;
              b |= (isBlack ? 1 : 0) << (7 - bit);
            }
            data[offset++] = b;
          }
        }
      } catch (e) {
        // Canvas was tainted by logo image - convert to image first
        try {
          // Create a temporary canvas to draw the image
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = width;
          tempCanvas.height = height;
          const tempCtx = tempCanvas.getContext('2d');

          // Draw the original canvas to temp canvas (this forces rendering)
          const imageData = canvas.toDataURL('image/png');
          const img2 = new Image();
          img2.crossOrigin = 'anonymous';

          await new Promise((resolve, reject) => {
            img2.onload = () => {
              tempCtx.drawImage(img2, 0, 0);
              resolve();
            };
            img2.onerror = reject;
            img2.src = imageData;
          });

          // Now get image data from temp canvas
          img = tempCtx.getImageData(0, 0, width, height);

          const xL = wBytes & 0xFF;
          const xH = (wBytes >> 8) & 0xFF;
          const yL = height & 0xFF;
          const yH = (height >> 8) & 0xFF;

          const header = [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH];
          data = new Uint8Array(header.length + wBytes * height);

          for (let i = 0; i < header.length; i++) data[i] = header[i];

          let offset = header.length;
          for (let row = 0; row < height; row++) {
            for (let byte = 0; byte < wBytes; byte++) {
              let b = 0;
              for (let bit = 0; bit < 8; bit++) {
                const x = byte * 8 + bit;
                if (x >= width) continue;
                const idx = (row * width + x) * 4;
                const r = img.data[idx];
                const g = img.data[idx + 1];
                const bl = img.data[idx + 2];
                const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * bl;
                const isBlack = luminance < 160;
                b |= (isBlack ? 1 : 0) << (7 - bit);
              }
              data[offset++] = b;
            }
          }
        } catch (fallbackError) {
          showError('Failed to process invoice with logo. Try disabling logo in settings.');
          return;
        }
      }

      const b64 = btoa(String.fromCharCode.apply(null, Array.from(data)));

      await salesOrderAPI.print(order.id, {
        printer_ip: ip,
        printer_port: parseInt(printerSettings.port || '9100', 10),
        data_base64: b64,
      });

      showSuccess('Invoice sent to printer successfully');
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      showError('Failed to print invoice: ' + errorMsg);
    } finally {
      setPrinting(false);
    }
  };

  const getItemName = (item) => {
    if (!item.inventory_item) return 'N/A';
    return i18n.language === 'ar' && item.inventory_item.item_name_ar
      ? item.inventory_item.item_name_ar
      : item.inventory_item.item_name;
  };

  const calculateItemDiscount = (item) => {
    if (!item.discount_id || !order.items) return 0;
    return parseFloat(item.discount_amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-xl text-gray-600">Order not found</p>
          <button onClick={() => navigate('/sales-orders')} className="mt-4 text-blue-600">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const subtotal =
    parseFloat(order.total_price || 0) -
    parseFloat(order.fee_amount || 0) +
    parseFloat(order.discount_amount || 0);

  const itemsDiscountTotal =
    order.items?.reduce((sum, item) => sum + calculateItemDiscount(item), 0) || 0;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice-print-area, #invoice-print-area * {
            visibility: visible;
          }
          #invoice-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
          .print-page {
            page-break-after: always;
          }
        }
      `}</style>

      {/* Action Buttons - Hidden when printing */}
      <div className="no-print bg-white border-b sticky top-0 z-10 shadow">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/sales-orders')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common.back')}
          </button>

          <button
            onClick={handlePrint}
            disabled={printing}
            className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {printing ? (
              <>
                <div className="w-5 h-5 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {t('common.printing') || 'Printing...'}
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                {t('salesOrders.printInvoice')}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview Tabs */}
      <div className="no-print bg-white border-b">
        <div className="max-w-6xl mx-auto px-6 flex gap-4">
          <button
            onClick={() => setShowPreview(false)}
            className={`py-4 px-6 font-medium border-b-2 transition-colors ${
              !showPreview
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Browser Invoice
          </button>
          <button
            onClick={() => setShowPreview(true)}
            className={`py-4 px-6 font-medium border-b-2 transition-colors ${
              showPreview
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Thermal Printer Preview
          </button>
        </div>
      </div>

      {/* Thermal Printer Preview */}
      {showPreview && previewCanvas && (
        <div className="no-print bg-gray-50 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white shadow-lg p-4 rounded border border-gray-200">
              <img
                src={previewCanvas.toDataURL()}
                alt="Thermal Printer Preview"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
            <p className="text-center text-gray-600 text-sm mt-4">
              This is how your invoice will look when printed on the thermal printer
            </p>
          </div>
        </div>
      )}

      {/* Browser Invoice Content */}
      {!showPreview && (
      <div id="invoice-print-area" className="bg-gray-50 min-h-screen py-8">
        <div className="max-w-4xl mx-auto bg-white shadow-lg" style={{ minHeight: '297mm' }}>
          <div className="p-12">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-4xl font-bold text-gray-900">{t('invoice.companyName')}</h1>
                <p className="text-gray-600 mt-1">{t('invoice.companyTagline')}</p>
              </div>
              <div className="text-right">
                <h2 className="text-3xl font-bold text-blue-600">{t('invoice.title')}</h2>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <div className="bg-gray-100 p-4 rounded">
                  <h3 className="font-bold text-gray-700 mb-3">{t('invoice.billTo')}:</h3>
                  <p className="font-semibold text-lg">{order.customer?.name || 'N/A'}</p>
                  {order.customer?.phone && <p className="text-gray-600">{t('customers.phone')}: {order.customer.phone}</p>}
                  {order.customer?.email && <p className="text-gray-600">{t('customers.email')}: {order.customer.email}</p>}
                  {/* {order.customer?.address && <p className="text-gray-600 mt-2">{order.customer.address}</p>} */}
                </div>
              </div>

              <div>
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">{t('common.date')}:</td>
                      <td className="py-2 text-right">{new Date(order.order_date).toLocaleDateString()}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">{t('invoice.invoiceNumber')}:</td>
                      <td className="py-2 text-right">{order.order_number}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 font-semibold">{t('invoice.customerId')}:</td>
                      <td className="py-2 text-right">{order.customer?.id || 'N/A'}</td>
                    </tr>
                    {order.expected_delivery_date && (
                      <tr className="border-b">
                        <td className="py-2 font-semibold">{t('salesOrders.expectedDeliveryDate')}:</td>
                        <td className="py-2 text-right">{new Date(order.expected_delivery_date).toLocaleDateString()}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="py-2 font-semibold">{t('common.status')}:</td>
                      <td className="py-2 text-right">
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                          {t(`salesOrders.statuses.${order.status}`)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-800 text-white">
                    <th className="text-left py-3 px-4">{t('invoice.description')}</th>
                    <th className="text-center py-3 px-4">{t('accounting.quantity')}</th>
                    <th className="text-right py-3 px-4">{t('salesOrders.unitPrice')}</th>
                    <th className="text-right py-3 px-4">{t('salesOrders.discount')}</th>
                    <th className="text-right py-3 px-4">{t('invoice.lineTotal')}</th>
                  </tr>
                </thead>

                <tbody>
                  {order.items && order.items.length > 0 ? (
                    order.items.map((item, index) => {
                      const itemSubtotal = item.quantity * item.unit_price;
                      const itemDiscount = calculateItemDiscount(item);
                      const lineTotal = itemSubtotal - itemDiscount;

                      return (
                        <tr key={index} className="border-b">
                          <td className="py-3 px-4">
                            <div className="font-medium">{getItemName(item)}</div>
                            {item.inventory_item?.item_code && (
                              <div className="text-sm text-gray-500">
                                {t('invoice.code')}: {item.inventory_item.item_code}
                              </div>
                            )}
                            {item.notes && (
                              <div className="text-sm text-gray-500 italic">{item.notes}</div>
                            )}
                          </td>

                          <td className="text-center py-3 px-4">
                            {parseFloat(item.quantity).toFixed(2)} {item.inventory_item?.unit || ''}
                          </td>

                          <td className="text-right py-3 px-4">
                            {t('common.currency')} {parseFloat(item.unit_price).toFixed(2)}
                          </td>

                          <td className="text-right py-3 px-4 text-red-600">
                            {itemDiscount > 0
                              ? `- ${t('common.currency')} ${itemDiscount.toFixed(2)}`
                              : '-'}
                          </td>

                          <td className="text-right py-3 px-4 font-semibold">
                            {t('common.currency')} {lineTotal.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-8 text-center text-gray-500">
                        {t('salesOrders.noItems')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-8">
              <div>
                {order.notes && (
                  <div className="bg-gray-50 p-4 rounded">
                    <h3 className="font-bold text-gray-700 mb-2">{t('invoice.specialNotes')}:</h3>
                    <p className="text-gray-600 text-sm">{order.notes}</p>
                  </div>
                )}
              </div>

              <div>
                <div className="space-y-2">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">{t('accounting.subtotal')}:</span>
                    <span className="font-semibold">
                      {t('common.currency')} {subtotal.toFixed(2)}
                    </span>
                  </div>

                  {itemsDiscountTotal > 0 && (
                    <div className="flex justify-between py-2 text-orange-600">
                      <span>{t('salesOrders.itemsDiscount')}:</span>
                      <span className="font-semibold">
                        - {t('common.currency')} {itemsDiscountTotal.toFixed(2)}
                      </span>
                    </div>
                  )}

                  {order.discount_amount > 0 && (
                    <div className="flex justify-between py-2 text-orange-600">
                      <span>{t('salesOrders.orderDiscount')}:</span>
                      <span className="font-semibold">
                        - {t('common.currency')} {parseFloat(order.discount_amount).toFixed(2)}
                      </span>
                    </div>
                  )}

                  {order.fee_amount > 0 && (
                    <div className="flex justify-between py-2 text-blue-600">
                      <span>{t('salesOrders.fee')}:</span>
                      <span className="font-semibold">
                        + {t('common.currency')} {parseFloat(order.fee_amount).toFixed(2)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between py-3 border-t-2 border-gray-800">
                    <span className="text-xl font-bold">{t('accounting.total')}:</span>
                    <span className="text-xl font-bold">
                      {t('common.currency')} {parseFloat(order.total_price || 0).toFixed(2)}
                    </span>
                  </div>

                  {/* Payment Status */}
                  <div className="mt-4 p-3 bg-gray-100 rounded">
                    <div className="flex justify-between mb-1">
                      <span className="font-semibold">{t('salesOrders.paymentStatus')}:</span>
                      <span
                        className={`font-bold ${
                          order.payment_status === 'paid'
                            ? 'text-green-600'
                            : order.payment_status === 'partial'
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}
                      >
                        {order.payment_status === 'paid'
                          ? t('invoice.paidInFull')
                          : order.payment_status === 'partial'
                          ? t('invoice.partialPayment')
                          : t('invoice.unpaid')}
                      </span>
                    </div>

                    {order.amount_paid > 0 && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">{t('salesOrders.amountPaid')}:</span>
                          <span className="text-green-600 font-semibold">
                            {t('common.currency')} {parseFloat(order.amount_paid).toFixed(2)}
                          </span>
                        </div>

                        {order.payment_status === 'partial' && (
                          <div className="flex justify-between text-sm mt-1">
                            <span className="text-gray-600">{t('salesOrders.remaining')}:</span>
                            <span className="text-red-600 font-semibold">
                              {t('common.currency')}{" "}
                              {(
                                parseFloat(order.total_price) -
                                parseFloat(order.amount_paid)
                              ).toFixed(2)}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t text-center">
              <p className="text-lg font-semibold mb-4">{t('invoice.thankYou')}</p>
              <p className="text-sm text-gray-600 mb-2">
                {t('invoice.inquiries')}
              </p>
              <p className="text-sm text-gray-600">
                {t('invoice.contactInfo')}
              </p>
            </div>
          </div>
        </div>
      </div>
      )}
    </>
  );
};

export default Invoice;
